import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { ReaderAvatar } from '@/components/ui/ReaderAvatar';
import { ReaderAvatarPicker } from '@/components/readers/ReaderAvatarPicker';
import { DEFAULT_READER_AVATAR_SLUG, type ReaderAvatarSlug } from '@/constants/reader-avatars';
import { readersService } from '@/services/readers.service';
import { useReadersStore } from '@/store/readers.store';
import { ApiError } from '@/services/http';
import type { ReaderView } from '@/types/api';

/**
 * AddReaderDialog — shared modal for "Add reader" actions from the Readers
 * page and the header dropdown. Keeps the dialog narrow (name only) to match
 * onboarding step 2 — interests and reading level are edited on the
 * ReaderEdit page.
 */
export interface AddReaderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the freshly-created reader after the store has been updated. */
  onCreated?: (reader: ReaderView) => void;
}

export function AddReaderDialog({ open, onOpenChange, onCreated }: AddReaderDialogProps) {
  const upsertReader = useReadersStore((s) => s.upsertReader);
  const setSelectedReaderId = useReadersStore((s) => s.setSelectedReaderId);

  const [name, setName] = useState('');
  const [avatarSlug, setAvatarSlug] = useState<ReaderAvatarSlug>(DEFAULT_READER_AVATAR_SLUG);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setName('');
    setAvatarSlug(DEFAULT_READER_AVATAR_SLUG);
    setSubmitting(false);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("What's your reader's name?");
      return;
    }
    setSubmitting(true);
    try {
      const reader = await readersService.create({ name: trimmed, avatarSlug });
      upsertReader(reader);
      setSelectedReaderId(reader.id);
      toast.success(`${reader.name} is ready to read!`);
      onCreated?.(reader);
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not create your reader.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-or-slate-900/55 backdrop-blur-sm" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-32px)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-or-slate-100 bg-white p-6 shadow-or-pop focus:outline-none">
          <Dialog.Title className="font-display text-[20px] font-bold text-or-slate-900">
            Add a reader
          </Dialog.Title>
          <Dialog.Description className="mt-1 text-[13px] text-or-slate-600">
            You'll be able to pick interests for them on the next screen.
          </Dialog.Description>
          <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-5">
            <div className="flex items-center gap-4">
              <ReaderAvatar name={name || '?'} avatarSlug={avatarSlug} size="lg" />
              <div className="flex-1">
                <FormField
                  label="First name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  maxLength={120}
                  placeholder="e.g. Ollie"
                />
              </div>
            </div>
            <ReaderAvatarPicker
              value={avatarSlug}
              onChange={setAvatarSlug}
              name={name}
              disabled={submitting}
            />
            <div className="flex items-center justify-end gap-2">
              <Dialog.Close asChild>
                <Button variant="secondary" size="md" disabled={submitting}>
                  Cancel
                </Button>
              </Dialog.Close>
              <Button type="submit" size="md" disabled={submitting}>
                {submitting ? 'Adding…' : 'Add reader'}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
